import React, { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { getDashboardRoute, getDashboardSection, isUserAllowed } from '../lib/authRouting'

interface ProtectedRouteProps {
  children: React.ReactNode
  allowedRoles: ('platform_admin' | 'org_admin' | 'employee' | 'individual')[]
}

export function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { isLoading, user } = useAuth()
  const location = useLocation()
  const [checking, setChecking] = useState(true)
  const [authorized, setAuthorized] = useState(false)

  useEffect(() => {
    if (isLoading) return;
    
    // Unauthenticated
    if (!user) {
      setAuthorized(false);
      setChecking(false);
      return;
    }

    // Check if user's type is in the allowed roles
    const userType = user.userType || 'employee'; // fallback for old users
    const isAuthorized = isUserAllowed(userType, allowedRoles as any);
    
    setAuthorized(isAuthorized);
    setChecking(false);
  }, [isLoading, user, allowedRoles]);

  if (isLoading || checking) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!authorized) {
    // If user is logged in but accessing wrong dashboard, redirect to their dashboard
    if (user?.userType) {
      const userType = user.userType
      const currentPath = location.pathname
      const correctSection = getDashboardSection(userType)
      
      // Don't redirect if already on correct dashboard section
      if (correctSection && currentPath.startsWith(correctSection)) {
        // Already on correct section but not authorized for this specific page
        return <Navigate to="/login" replace />
      }
      
      // Redirect to correct dashboard using centralized utility
      const dashboardRoute = getDashboardRoute(userType)
      return <Navigate to={dashboardRoute} replace />
    }
    
    // Not logged in at all
    // If trying to access platform routes, redirect to platform login
    const isPlatformRoute = allowedRoles.includes('platform_admin') && allowedRoles.length === 1;
    const loginPath = isPlatformRoute ? '/platform/login' : '/login';
    return <Navigate to={loginPath} replace state={{ from: location.pathname }} />
  }

  return <>{children}</>
}
